import type { MeetingDetail, MeetingProposalDto } from '@fos/shared/contracts/meetings';
import { meetingTasksCount } from '@fos/shared/labels';
import { Check, Pencil } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage, validationIssues } from '../../../lib/api';
import { firstName, formatDateShort, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { AIBlock, FormMessage } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { toast } from '../../../ui/Toast';

/**
 * Propozycje zadań od AI ze spotkania (DESIGN §5 „Propozycje AI”, Figma 05): blok na accent/subtle z ikoną sparkles,
 * lista z checkboxami, „Dodaj N zadania” (Primary) i „Odrzuć resztę”, źródło jako link do transkrypcji.
 * Przed dodaniem można zmienić tytuł, wykonawcę i termin propozycji. Rozstrzygnięte propozycje zostają pod listą
 * (dodane jako linki do zadań).
 */

interface ProposalEdit {
  title?: string;
  assigneeUserId?: string | null;
  dueDate?: string | null;
}

const NONE = '__brak__';

/** Wartości propozycji po zmianach przed przyjęciem. */
function effective(p: MeetingProposalDto, edit: ProposalEdit | undefined, members: Map<string, string>) {
  const assigneeUserId = edit?.assigneeUserId !== undefined ? edit.assigneeUserId : (p.assignee?.userId ?? null);
  const assigneeName =
    assigneeUserId === null
      ? null
      : (members.get(assigneeUserId) ?? (p.assignee?.userId === assigneeUserId ? p.assignee.name : null));
  return {
    title: edit?.title ?? p.title,
    assigneeUserId,
    assigneeName,
    dueDate: edit?.dueDate !== undefined ? edit.dueDate : p.dueDate,
  };
}

/** Linia pod tytułem (Figma 05): „HelpMyCar · Aplikacja · Seban”, „… · do 7.10”, „HelpMyCar · bez terminu”. */
export function proposalMetaText(
  meeting: Pick<MeetingDetail, 'client' | 'project'>,
  value: { assigneeName: string | null; dueDate: string | null },
): string {
  const due = value.dueDate ? `do ${formatDateShort(value.dueDate)}` : value.assigneeName ? null : 'bez terminu';
  return [meeting.client?.name, meeting.project?.name, value.assigneeName ? firstName(value.assigneeName) : null, due]
    .filter(Boolean)
    .join(' · ');
}

export function ProposalsBlock({ meeting }: { meeting: MeetingDetail }) {
  const utils = trpc.useUtils();
  const pending = useMemo(() => meeting.proposals.filter((p) => p.status === 'proposed'), [meeting.proposals]);
  const accepted = meeting.proposals.filter((p) => p.status === 'accepted');
  const rejected = meeting.proposals.filter((p) => p.status === 'rejected');
  const canDecide = meeting.canDecideProposals;

  const [selected, setSelected] = useState<Set<string>>(() => new Set(pending.map((p) => p.id)));
  const [edits, setEdits] = useState<Record<string, ProposalEdit>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [blockError, setBlockError] = useState<string | null>(null);

  // nowe propozycje (ponowne przetworzenie) są domyślnie zaznaczone; rozstrzygnięte znikają z wyboru
  const knownIds = useRef(new Set(pending.map((p) => p.id)));
  const pendingKey = pending.map((p) => p.id).join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy zmianie listy czekających propozycji
  useEffect(() => {
    const known = knownIds.current;
    const fresh = pending.filter((p) => !known.has(p.id)).map((p) => p.id);
    for (const id of fresh) known.add(id);
    const ids = new Set(pending.map((p) => p.id));
    setSelected((prev) => new Set([...[...prev].filter((id) => ids.has(id)), ...fresh]));
  }, [pendingKey]);

  const options = trpc.meetings.formOptions.useQuery(
    { clientId: meeting.client?.id ?? null },
    { enabled: canDecide && pending.length > 0 },
  );
  const members = useMemo(() => new Map((options.data?.members ?? []).map((m) => [m.userId, m.name])), [options.data]);

  const accept = trpc.meetings.acceptProposals.useMutation();
  const reject = trpc.meetings.rejectProposals.useMutation();
  const busy = accept.isPending || reject.isPending;

  const chosen = pending.filter((p) => selected.has(p.id));
  const rest = pending.filter((p) => !selected.has(p.id));

  const refresh = async () => {
    await Promise.all([utils.meetings.invalidate(), utils.tasks.invalidate()]);
  };

  const showErrors = (err: unknown, ids: readonly string[], fallback: string) => {
    const byRow: Record<string, string> = {};
    for (const issue of validationIssues(err)) {
      const match = /^ids\.(\d+)/.exec(issue.path);
      const id = match ? ids[Number(match[1])] : undefined;
      if (id && !byRow[id]) byRow[id] = issue.message;
    }
    setRowErrors(byRow);
    setBlockError(Object.keys(byRow).length > 0 ? null : errorMessage(err, fallback));
  };

  const onAccept = async () => {
    if (chosen.length === 0) return;
    setRowErrors({});
    setBlockError(null);
    const ids = chosen.map((p) => p.id);
    const changes = ids
      .map((id) => ({ id, ...edits[id] }))
      .filter((e) => Object.keys(e).length > 1)
      .map((e) => ({ ...e, version: pending.find((p) => p.id === e.id)?.version }));
    try {
      const res = await accept.mutateAsync({
        meetingId: meeting.id,
        ids,
        ...(changes.length > 0 ? { edits: changes } : {}),
      });
      await refresh();
      setEdits((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !ids.includes(id))));
      const added = res.accepted.length;
      toast.success(added > 0 ? `Dodano ${meetingTasksCount(added)}` : 'Te propozycje są już dodane', {
        ...(added > 0 ? { description: res.accepted.map((a) => `#${a.taskNumber}`).join(', ') } : {}),
      });
    } catch (err) {
      showErrors(err, ids, 'Nie udało się dodać zadań.');
    }
  };

  const onRejectRest = async () => {
    if (rest.length === 0) return;
    setRowErrors({});
    setBlockError(null);
    const ids = rest.map((p) => p.id);
    try {
      await reject.mutateAsync({ meetingId: meeting.id, ids });
      await refresh();
      toast.success(`Odrzucono ${rest.length} ${plural(rest.length, ['propozycję', 'propozycje', 'propozycji'])}`);
    } catch (err) {
      showErrors(err, ids, 'Nie udało się odrzucić propozycji.');
    }
  };

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const editing = pending.find((p) => p.id === editingId) ?? null;
  const title = pending.length > 0 ? `AI proponuje ${meetingTasksCount(pending.length)}` : 'Propozycje zadań z AI';

  return (
    <AIBlock
      title={
        <span className="flex w-full items-baseline justify-between gap-3">
          <span>{title}</span>
          {pending.length > 0 && canDecide && (
            <span aria-hidden className="hidden text-caption font-normal text-fg-muted sm:inline">
              wybierz, które dodać
            </span>
          )}
        </span>
      }
      className="[&_h3]:flex-1"
      actions={
        pending.length > 0 && canDecide ? (
          <>
            <Button
              variant="primary"
              icon={Check}
              onClick={() => void onAccept()}
              disabled={chosen.length === 0 || busy}
              loading={accept.isPending}
            >
              {chosen.length > 0 ? `Dodaj ${meetingTasksCount(chosen.length)}` : 'Dodaj zadania'}
            </Button>
            {rest.length > 0 && (
              <Button variant="ghost" onClick={() => void onRejectRest()} disabled={busy} loading={reject.isPending}>
                {chosen.length === 0 ? 'Odrzuć wszystkie' : 'Odrzuć resztę'}
              </Button>
            )}
          </>
        ) : undefined
      }
      footnote={
        <>
          Źródło:{' '}
          <a href="#transkrypcja" className="text-accent hover:underline">
            {meeting.hasTranscript ? 'transkrypcja spotkania' : 'notatki ze spotkania'}
          </a>
          {meeting.ai.simulated && !meeting.summaryMd && ' · symulacja (bez dostawcy AI)'}
          {!canDecide && pending.length > 0 && ' · dodawać zadania mogą osoby z edycją Zadań'}
        </>
      }
    >
      {blockError && (
        <div className="mb-2">
          <FormMessage>{blockError}</FormMessage>
        </div>
      )}
      {pending.length > 0 && (
        <ul aria-label="Propozycje zadań" className="mt-1">
          {pending.map((p) => {
            const value = effective(p, edits[p.id], members);
            const meta = proposalMetaText(meeting, value);
            const rowError = rowErrors[p.id];
            return (
              <li key={p.id} className="flex items-start gap-3 border-t border-line-strong py-3">
                {canDecide ? (
                  <Checkbox
                    className="mt-1"
                    checked={selected.has(p.id)}
                    onCheckedChange={(on) => toggle(p.id, on)}
                    id={`propozycja-${p.id}`}
                  />
                ) : (
                  <span aria-hidden className="mt-1 size-4 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  {canDecide ? (
                    <label
                      htmlFor={`propozycja-${p.id}`}
                      className="block cursor-pointer text-body-s font-medium break-words text-fg"
                    >
                      {value.title}
                    </label>
                  ) : (
                    <p className="text-body-s font-medium break-words text-fg">{value.title}</p>
                  )}
                  {meta && <p className="text-caption text-fg-muted">{meta}</p>}
                  {p.description && <p className="mt-1 text-caption break-words text-fg-secondary">{p.description}</p>}
                  {rowError && (
                    <p role="alert" className="mt-1 text-caption text-danger">
                      {rowError}
                    </p>
                  )}
                </div>
                {canDecide && (
                  <IconButton
                    icon={Pencil}
                    label={`Zmień propozycję: ${value.title}`}
                    iconSize={15}
                    onClick={() => setEditingId(p.id)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {accepted.length > 0 && (
        <div className={pending.length > 0 ? 'mt-3' : ''}>
          <p className="text-caption font-medium text-fg-muted">Dodane do zadań</p>
          <ul aria-label="Dodane do zadań" className="mt-1 flex flex-col gap-1">
            {accepted.map((p) => (
              <li key={p.id} className="flex min-w-0 items-baseline gap-2 text-body-s">
                <Check size={14} strokeWidth={1.75} aria-hidden className="shrink-0 self-center text-success" />
                {p.task ? (
                  <Link to={`/zadania/${p.task.id}`} className="min-w-0 truncate text-fg hover:underline">
                    <Mono className="text-fg-muted">#{p.task.number}</Mono> {p.task.title}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate text-fg">{p.title}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {rejected.length > 0 && (
        <p className="mt-2 text-caption text-fg-muted">
          Odrzucone: {rejected.length} {plural(rejected.length, ['propozycja', 'propozycje', 'propozycji'])}
        </p>
      )}
      {editing && (
        <ProposalEditDialog
          open={editing !== null}
          onOpenChange={(open) => {
            if (!open) setEditingId(null);
          }}
          proposal={editing}
          value={effective(editing, edits[editing.id], members)}
          members={options.data?.members ?? []}
          onSave={(edit) => {
            setEdits((prev) => ({ ...prev, [editing.id]: edit }));
            setRowErrors((prev) => {
              const { [editing.id]: _, ...restErrors } = prev;
              return restErrors;
            });
            setSelected((prev) => new Set(prev).add(editing.id));
          }}
        />
      )}
    </AIBlock>
  );
}

/** Zmiana tytułu, wykonawcy i terminu propozycji przed dodaniem (zapis razem z „Dodaj N”). */
function ProposalEditDialog({
  open,
  onOpenChange,
  proposal,
  value,
  members,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposal: MeetingProposalDto;
  value: { title: string; assigneeUserId: string | null; dueDate: string | null };
  members: { userId: string; name: string }[];
  onSave: (edit: ProposalEdit) => void;
}) {
  const [title, setTitle] = useState(value.title);
  const [assignee, setAssignee] = useState<string | null>(value.assigneeUserId);
  const [due, setDue] = useState(value.dueDate ?? '');
  const [error, setError] = useState<string | null>(null);

  const assigneeOptions: ComboboxOption[] = [
    { value: NONE, label: 'Bez wykonawcy' },
    ...members.map((m) => ({ value: m.userId, label: m.name })),
  ];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setError('Tytuł jest wymagany');
      return;
    }
    const edit: ProposalEdit = {};
    if (title.trim() !== proposal.title) edit.title = title.trim();
    if (assignee !== (proposal.assignee?.userId ?? null)) edit.assigneeUserId = assignee;
    if ((due || null) !== proposal.dueDate) edit.dueDate = due || null;
    onSave(edit);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Zmień propozycję"
      description="Zmiany zapiszą się razem z dodaniem zadania."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="proposal-form">
            Gotowe
          </Button>
        </>
      }
    >
      <form id="proposal-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label="Tytuł zadania" error={error}>
          <Input
            autoFocus
            value={title}
            maxLength={300}
            onChange={(e) => {
              setTitle(e.target.value);
              setError(null);
            }}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Wykonawca">
            <Combobox
              value={assignee ?? NONE}
              onValueChange={(v) => setAssignee(v === NONE ? null : v)}
              options={assigneeOptions}
              searchPlaceholder="Szukaj osoby…"
            />
          </Field>
          <Field label="Termin">
            <DateInput value={due} onValueChange={setDue} />
          </Field>
        </div>
      </form>
    </Dialog>
  );
}
